/**
 * Condition producibility (D184): "no reading waits on a fact this pack can
 * never make true" — the D31 dead-content law, applied to the leaf
 * conditions the constant-condition check never decided.
 *
 * @remarks
 * `findConstantBinaryStateCondition` (D56) decides two of the seventeen leaf
 * condition types — `container_open`, `switched_on` — in one case each: the
 * target declares no such property. Calder's audit of the gates (2026-09-14,
 * §1 and §3) measured the other fifteen by hand across the three packs and
 * found them clean, then asked for the analysis to live in the loader, where
 * the second pack's author will meet it. This is that analysis, at the same
 * tier `occurrence-producibility` calls **static**: judged from the parsed
 * document alone, with the same one-way soundness — a producer that EXISTS
 * declines to condemn, whether or not anything in play can ever run it.
 * Chasing that is simulation-shaped, and the abstention is deliberate.
 *
 * What each leaf needs, to be producible:
 *
 * - `flag_equals(f, v)` — `v` is the flag's `initialValue`, or a `set_flag`
 *   somewhere writes `v`.
 * - `has_item(i)` / `item_acquired(i)` — the player starts with it, an
 *   `add_item_to_inventory` grants it, or it is takeable (portable, not
 *   ambient) and stands somewhere in the world.
 * - `item_in_room(i, r)` — it stands in `r` (directly, or inside a container
 *   that does), or it is takeable at all, because a carried thing can be
 *   dropped in any room the player reaches (abstained, not proven).
 * - `item_equipped(i)` — the item declares `equippable`, and can be held.
 * - `item_spent(i)` — the item declares `durability`.
 * - `counter_at_least(c, k)` (D244) — the counter can be carried onto `k`
 *   AND off it: judged both ways, with polarity, by the counter layer
 *   (`counter-validation.ts`), because a counter read is dead whichever
 *   answer it is stuck on.
 * - `event_fired(e)` / `event_pending(e)` — a `schedule_event` names it.
 * - `actor_present(a)` / `actor_addressable(a)` (D232) — the actor is ever
 *   on stage: it declares an `initialRoomId`, or a `move_actor` carries it
 *   onto a room (D233).
 * - `actor_in_room(a, r)` (D233) — `r` is the actor's `initialRoomId`, or a
 *   `move_actor` names `r` for it, or (D238) the actor follows and the
 *   player can lead it into `r`.
 * - `actor_attacked(a)` / `actor_defeated(a)` (D234) — the actor declares
 *   `combat`: only a foe is struck, and only a foe falls. (And this walk is
 *   what decides Mike's ruling 3 for `hostileWhen` itself: a hostility the
 *   pack can never make true is a refused pack, so every foe that loads can
 *   be fought.)
 *
 * `voice_line_spoken`, `ending_selected`, `switched_on` and `container_open`
 * are already held by their own rules (R12, D31, D56), and `item_found`
 * (D239) by the reference walk's (only a concealed thing is ever found);
 * `item_searched`, `log_read`, `room_equals`, the three sentence conditions — D219's
 * `indirect_object_is`, D226's `direct_object_is`, D229's `tool_is` (the
 * player can name any thing in any slot) — and `occurrence_tally_at_least`
 * are either always
 * producible on a valid pack (the declared thing exists; whether the player
 * reaches it is simulation-shaped) or judged elsewhere. A leaf whose subject
 * is undeclared is skipped here — the reference walk already names it.
 *
 * Polarity is tracked through `not`: an unproducible leaf under a `not` is a
 * condition that ALWAYS holds, and the failure says so, because the two
 * defects read differently to the author (a reading never chosen, against a
 * reading that shadows everything after it). Either way the pack shipped a
 * question with one answer, which is the defect (Mike's ruling on
 * proven-unproducible content, 2026-08-22: a hard failure, not a warning).
 *
 * Conditions are found by structural walk keyed on the discriminant, so the
 * check cannot go stale when conditions grow a new home.
 *
 * @packageDocumentation
 */

import { type ConditionDefinition, containerActorIdOf, containerItemIdOf, type ContentPackDefinition, type CounterDefinition, roomsAnActorCanStandIn, stageDirectionsOf } from '@orpheus/content-schema';

import { type CounterMoves, counterMovesOf, unproducibleCounterReadingOf } from './counter-validation.js';
import { everyEffectOf } from './effect-sources.js';
import { type ReferenceBrokenFailure } from './load-failure.js';
import { brokenReference } from './semantic-validation.js';
import { fieldOf, walkDocumentObjects } from './structural-walk.js';

/** Every condition discriminant — a walked object with one of these at `type` is a condition root. */
const CONDITION_TYPES: ReadonlySet<string> = new Set([
	'actor_addressable',
	'actor_attacked',
	'actor_defeated',
	'actor_in_room',
	'actor_present',
	'all_of',
	'any_of',
	'at_least_of',
	'container_open',
	// D244: an amount at a threshold — judged with its polarity by the counter layer.
	'counter_at_least',
	'ending_selected',
	'event_fired',
	'event_pending',
	'flag_equals',
	'has_item',
	'direct_object_is',
	'indirect_object_is',
	'item_acquired',
	'item_equipped',
	'item_found',
	'item_in_room',
	'item_searched',
	'item_spent',
	'log_read',
	'not',
	'occurrence_tally_at_least',
	// D244: the body's amount — whole at genesis, so only a foe can carry it below a threshold.
	'player_health_at_least',
	// D241: a root like any other leaf — held by its own rules (`record-validation.ts`); its `where` is judged as ordinary conditions, because a layer can only remember what the run could make true.
	'record_holds',
	'room_equals',
	'switched_on',
	'tool_is',
	'voice_line_spoken',
]);

type Item = ContentPackDefinition['items'][number];

/** One declared item, with the rooms it stands in and whether it can be taken — everything an item leaf asks. */
interface StandingItem {
	readonly isTakeable: boolean;
	readonly item: Item;
	readonly roomIds: ReadonlySet<string>;
}

/** D235: where one body can stand, and where it is heard from — the two answers the actor leaves read. */
interface ActorStage {
	readonly heardFrom: readonly string[];
	readonly standsIn: readonly string[];
}

/** What the pack can make true, read once. */
interface World {
	/** D244: the declared counters, and how the pack can move each. */
	readonly counterMoves: ReadonlyMap<string, CounterMoves>;
	readonly counters: ReadonlyMap<string, CounterDefinition>;
	readonly flagInitialValues: ReadonlyMap<string, boolean>;
	/** D244: the player's whole health, when the player has a body. */
	readonly grantedItemIds: ReadonlySet<string>;
	readonly itemsById: ReadonlyMap<string, StandingItem>;
	readonly playerHealth: number | undefined;
	readonly scheduledEventIds: ReadonlySet<string>;
	readonly setFlagWrites: ReadonlySet<string>;
	/** D234: the actors that are FOES — declaring `combat` — the only bodies ever struck or fallen. */
	readonly foeActorIds: ReadonlySet<string>;
	/**
	 * D232/D233: the rooms each actor can EVER stand in — its `initialRoomId`
	 * and every `move_actor` destination, and (D238) for a follower every room
	 * the player can lead it to; empty for a body never on stage —
	 * and (D235) the rooms it is HEARD from, its `audibleFrom`; empty for a
	 * body only ever met where it stands.
	 */
	readonly stagesByActorId: ReadonlyMap<string, ActorStage>;
}

/** The rooms an item nowhere stands in. */
const NO_ROOMS: ReadonlySet<string> = new Set();

/** The key a flag write or read is judged by. */
const flagWriteKey = (flagId: string, value: boolean): string => `${flagId}:${String(value)}`;

/**
 * The rooms an item stands in — directly placed, or inside a container that
 * is, following `containedIn` up to a placed thing (D240: or up to a body,
 * which stands wherever `roomsAnActorCanStandIn` says).
 *
 * @param contentPack - The parsed pack
 * @returns Each placed item's rooms; an item nowhere has no entry
 */
const roomIdsByItemOf = (contentPack: ContentPackDefinition): ReadonlyMap<string, ReadonlySet<string>> => {
	const direct = new Map<string, Set<string>>();

	for (const room of contentPack.rooms) {
		for (const itemId of room.itemPlacements) {
			(direct.get(itemId) ?? direct.set(itemId, new Set()).get(itemId))?.add(room.roomId);
		}
	}

	const itemsById = new Map(contentPack.items.map((item) => [item.itemId as string, item]));
	const stage = stageDirectionsOf(contentPack);
	const roomsOf = (itemId: string, depth: number): ReadonlySet<string> => {
		const placed = direct.get(itemId);
		const containedIn = itemsById.get(itemId)?.containedIn;
		const carryingActor = contentPack.actors.find((actor) => actor.actorId === containerActorIdOf(containedIn));

		// D240: a thing a body carries is wherever the body can stand.
		if (carryingActor !== undefined) {
			return new Set(roomsAnActorCanStandIn(carryingActor, stage));
		}

		const container = containerItemIdOf(containedIn);

		// A placed thing is where it is placed; a contained thing is where its
		// container is. `depth` bounds a containment cycle, which the
		// containment rule refuses on its own — this walk only has to survive it.
		if (placed !== undefined || container === undefined || depth > itemsById.size) {
			return placed ?? new Set();
		}

		return roomsOf(container, depth + 1);
	};

	return new Map(contentPack.items.flatMap((item) => {
		const roomIds = roomsOf(item.itemId, 0);

		return roomIds.size === 0 ? [] : [[item.itemId, roomIds] as const];
	}));
};

/**
 * Read the pack once into the facts every leaf is judged against.
 *
 * @param contentPack - The parsed pack
 * @returns The world's producers
 */
const worldOf = (contentPack: ContentPackDefinition): World => {
	const effects = everyEffectOf(contentPack);
	const stage = stageDirectionsOf(contentPack);
	const roomIdsByItem = roomIdsByItemOf(contentPack);

	return {
		counterMoves: counterMovesOf(contentPack),
		counters: new Map(contentPack.counters.map((counter) => [counter.counterId as string, counter])),
		flagInitialValues: new Map(contentPack.flags.map((flag) => [flag.flagId as string, flag.initialValue])),
		foeActorIds: new Set(contentPack.actors.flatMap((actor) => (actor.combat === undefined ? [] : [actor.actorId as string]))),
		grantedItemIds: new Set([
			...contentPack.manifest.initialInventory,
			...effects.flatMap((effect) => (effect.type === 'add_item_to_inventory' ? [effect.itemId as string] : [])),
		]),
		itemsById: new Map(
			contentPack.items.map((item) => [
				item.itemId as string,
				{
					// D19b: takeable is portable and not ambient — the take rule's own test.
					isTakeable: item.isPortable && !contentPack.ambientItemIds.includes(item.itemId),
					item,
					roomIds: roomIdsByItem.get(item.itemId) ?? NO_ROOMS,
				},
			]),
		),
		playerHealth: contentPack.manifest.player?.health,
		scheduledEventIds: new Set(effects.flatMap((effect) => (effect.type === 'schedule_event' ? [effect.eventId as string] : []))),
		setFlagWrites: new Set(effects.flatMap((effect) => (effect.type === 'set_flag' ? [flagWriteKey(effect.flagId, effect.value)] : []))),
		stagesByActorId: new Map(
			contentPack.actors.map((actor) => [actor.actorId as string, { heardFrom: actor.audibleFrom, standsIn: roomsAnActorCanStandIn(actor, stage) }]),
		),
	};
};

/** Whether the player can ever come to hold the item. */
const canBeHeld = (world: World, standing: StandingItem): boolean =>
	world.grantedItemIds.has(standing.item.itemId) || (standing.isTakeable && standing.roomIds.size > 0);

/** A leaf that cannot hold, and the subject it names; nothing when it can, or when the question is not this module's. */
interface Unproducible {
	readonly reason: string;
	readonly targetId: string;
}

/**
 * Why a leaf can never hold — or nothing.
 *
 * @param leaf - The condition, already known not to be a combinator
 * @param world - The pack's producers
 * @returns The reason and the subject, when the leaf is proven unproducible
 */
const unproducibleLeafOf = (leaf: ConditionDefinition, world: World): Unproducible | undefined => {
	switch (leaf.type) {
		case 'actor_addressable':
		case 'actor_attacked':
		case 'actor_defeated':
		case 'actor_in_room':
		case 'actor_present': {
			return unproducibleActorLeafOf(leaf, world);
		}
		case 'event_fired':
		case 'event_pending': {
			return world.scheduledEventIds.has(leaf.eventId)
				? undefined
				: { reason: 'no schedule_event anywhere names this event, so it never fires and is never pending', targetId: leaf.eventId };
		}
		case 'flag_equals': {
			const isInitiallyTrue = world.flagInitialValues.get(leaf.flagId);

			if (isInitiallyTrue === undefined || isInitiallyTrue === leaf.expectedValue || world.setFlagWrites.has(flagWriteKey(leaf.flagId, leaf.expectedValue))) {
				return undefined;
			}

			return {
				reason: `the flag starts ${String(isInitiallyTrue)} and no set_flag ever writes ${String(leaf.expectedValue)}, so this read never sees the value it asks for`,
				targetId: leaf.flagId,
			};
		}
		case 'has_item':
		case 'item_acquired':
		case 'item_equipped':
		case 'item_in_room':
		case 'item_spent': {
			return unproducibleItemLeafOf(leaf, world);
		}
		default: {
			return undefined;
		}
	}
};

type ActorLeaf = Extract<ConditionDefinition, { readonly type: 'actor_addressable' | 'actor_attacked' | 'actor_defeated' | 'actor_in_room' | 'actor_present' }>;

/**
 * The actor-shaped leaves, judged together (split out at the complexity cap
 * when D234 added the fourth and fifth): each asks something of one declared
 * body, and an undeclared one is the reference walk's to name.
 *
 * @param leaf - The condition
 * @param world - The pack's producers
 * @returns The reason and the actor, when the leaf is proven unproducible
 */
const unproducibleActorLeafOf = (leaf: ActorLeaf, world: World): Unproducible | undefined => {
	const stage = world.stagesByActorId.get(leaf.actorId);
	const targetId = leaf.actorId;

	if (stage === undefined) {
		return undefined;
	}

	const { heardFrom, standsIn } = stage;

	switch (leaf.type) {
		// D232: an actor nowhere on stage is never heard — unless (D235) some
		// room hears it by `audibleFrom`: a voice with a location that is not
		// the player's is addressable from there without ever being present.
		case 'actor_addressable': {
			return standsIn.length > 0 || heardFrom.length > 0
				? undefined
				: { reason: 'the actor declares no `initialRoomId`, no move_actor carries it onto a room, and no room hears it, so it is never here', targetId };
		}
		// D234: only a foe is struck, and only a foe falls.
		case 'actor_attacked':
		case 'actor_defeated': {
			return world.foeActorIds.has(leaf.actorId) ? undefined : { reason: 'the actor declares no `combat`, so it is never struck and never falls', targetId };
		}
		// D233: a body stands in a room only if the pack ever puts it there —
		// D238: or the player can lead it there, if it follows.
		case 'actor_in_room': {
			return standsIn.includes(leaf.roomId)
				? undefined
				: { reason: 'the actor neither starts in that room nor is ever moved to it by a move_actor, nor can the player lead it there, so it never stands there', targetId };
		}
		// D232: an actor nowhere on stage is never present.
		case 'actor_present': {
			return standsIn.length > 0
				? undefined
				: { reason: 'the actor declares no `initialRoomId` and no move_actor carries it onto a room, so it is never here', targetId };
		}
	}
};

type ItemLeaf = Extract<ConditionDefinition, { readonly type: 'has_item' | 'item_acquired' | 'item_equipped' | 'item_in_room' | 'item_spent' }>;

/**
 * The item-shaped leaves, judged together: each asks something of one
 * declared item, and an undeclared one is the reference walk's to name.
 *
 * @param leaf - The condition
 * @param world - The pack's producers
 * @returns The reason and the item, when the leaf is proven unproducible
 */
const unproducibleItemLeafOf = (leaf: ItemLeaf, world: World): Unproducible | undefined => {
	const standing = world.itemsById.get(leaf.itemId);

	if (standing === undefined) {
		return undefined;
	}

	const { item } = standing;
	const targetId = leaf.itemId;
	const isHoldable = canBeHeld(world, standing);
	const notHoldable = 'the player never holds it — it starts in no inventory, no add_item_to_inventory grants it, and it is not a takeable thing standing somewhere in the world';

	switch (leaf.type) {
		case 'has_item':
		case 'item_acquired': {
			return isHoldable ? undefined : { reason: notHoldable, targetId };
		}
		case 'item_equipped': {
			if (item.equippable === undefined) {
				return { reason: 'the item declares no `equippable`, so it is never worn', targetId };
			}

			return isHoldable ? undefined : { reason: `it is never worn because ${notHoldable}`, targetId };
		}
		case 'item_in_room': {
			return standing.roomIds.has(leaf.roomId) || standing.isTakeable
				? undefined
				: { reason: `it never stands in ${leaf.roomId} — not placed there, not inside anything placed there, and not takeable, so it cannot be carried in`, targetId };
		}
		case 'item_spent': {
			return item.durability === undefined ? { reason: 'the item declares no `durability`, so it is never spent', targetId } : undefined;
		}
	}
};

/**
 * Judge one condition tree: every leaf, with the polarity the `not` wrappers
 * above it give it.
 *
 * @param condition - The tree
 * @param path - Its path in the document
 * @param isNegated - Whether an odd number of `not` wrappers enclose it
 * @param world - The pack's producers
 * @param failures - Accumulator
 * @param seen - Every condition node already judged, so the structural walk does not judge a subtree twice
 */
const judgeCondition = (
	condition: ConditionDefinition,
	path: string,
	isNegated: boolean,
	world: World,
	failures: ReferenceBrokenFailure[],
	seen: WeakSet<object>,
): void => {
	seen.add(condition);

	switch (condition.type) {
		// D229: `at_least_of` walks as `any_of` does — every member judged,
		// with the polarity of the wrappers above it.
		case 'all_of':
		case 'any_of':
		case 'at_least_of': {
			condition.conditions.forEach((nested, index) => {
				judgeCondition(nested, `${path}.conditions[${String(index)}]`, isNegated, world, failures, seen);
			});

			return;
		}
		// D244: a counter reading can be dead both ways, so the counter layer judges it with its polarity and words the whole failure.
		case 'counter_at_least': {
			const complaint = unproducibleCounterReadingOf(condition, isNegated, world.counters, world.counterMoves);

			if (complaint !== undefined) {
				failures.push(brokenReference(path, condition.counterId, complaint));
			}

			return;
		}
		case 'not': {
			judgeCondition(condition.condition, `${path}.condition`, !isNegated, world, failures, seen);

			return;
		}
		// D244: the body starts whole, so "at least k" holds at genesis; only a foe can make it fail.
		case 'player_health_at_least': {
			if (world.playerHealth !== undefined && condition.minimumCount <= world.playerHealth && world.foeActorIds.size === 0) {
				failures.push(
					brokenReference(
						path,
						'player_health_at_least',
						`no actor declares \`combat\`, so nothing ever wounds the player and "at least ${String(condition.minimumCount)}" always holds — ${isNegated ? 'under `not`, this condition can never hold, and whatever it guards is never reached' : 'this condition ALWAYS holds, and whatever it guards shadows everything after it'}`,
					),
				);
			}

			return;
		}
		default: {
			const unproducible = unproducibleLeafOf(condition, world);

			if (unproducible !== undefined) {
				failures.push(
					brokenReference(
						path,
						unproducible.targetId,
						isNegated
							? `${unproducible.reason} — under \`not\`, this condition ALWAYS holds, and whatever it guards shadows everything after it`
							: `${unproducible.reason} — this condition can never hold, and whatever it guards is never reached`,
					),
				);
			}
		}
	}
};

/**
 * Every leaf condition in the pack that the pack can never make true.
 *
 * @param contentPack - The parsed pack
 * @returns One failure per unproducible leaf, in document order
 */
export const validateConditionProducibility = (contentPack: ContentPackDefinition): readonly ReferenceBrokenFailure[] => {
	const world = worldOf(contentPack);
	const failures: ReferenceBrokenFailure[] = [];
	const seen = new WeakSet<object>();

	walkDocumentObjects(contentPack, (record, path) => {
		const type = fieldOf(record, 'type');

		// A condition root: a condition-shaped node the walk has not already
		// reached from an enclosing one. The walk is pre-order, so a root is
		// judged — and its subtree marked — before the walk descends into it.
		if (typeof type === 'string' && CONDITION_TYPES.has(type) && !seen.has(record)) {
			judgeCondition(record as unknown as ConditionDefinition, path, false, world, failures, seen);
		}
	});

	return failures;
};
